/**
 * MemberProfileEdit - Profile editing model with string-based tag names
 */
export interface IMemberProfileEdit {
  // Basic Info
  FirstName?: string | null;

  // Private Info
  BirthDate?: string | null; // ISO date string

  // Stats (Primary Info)
  HeightCm?: number | null;
  WeightKg?: number | null;
  WeightGoalKg?: number | null;

  // Bio
  Description?: string | null;

  // About Me (single-select as single-item arrays)
  Ethnicities?: string[] | null;
  TextField1?: string | null;
  TextField2?: string | null;
  TextField3?: string | null;

  // Identity tags (multi-select)
  SocialTags?: string[] | null;
  Genders?: string[] | null;
  InterestedIn?: string[] | null;
  Relationships?: string[] | null;
  Languages?: string[] | null;

  // Interests and Kinks (multi-select)
  LookingFor?: string[] | null;
  SexualInterests?: string[] | null;
  CommunityKinks?: string[] | null;
  Kinks?: string[] | null;

  // Body types and features
  BodyTypes?: string[] | null; // Single-select (MaxLength 1)
  BodyTypesLiked?: string[] | null;
  BodyPartsLiked?: string[] | null;
  BodyHair?: string[] | null; // Single-select (MaxLength 1)
  FacialHair?: string[] | null; // Single-select (MaxLength 1)
}

/**
 * ProfileOptions - Available options for profile editing fields
 */
export interface IProfileOptions {
  // Identity tags
  SocialTags?: string[] | null;
  Genders?: string[] | null;
  InterestedIn?: string[] | null;
  Relationships?: string[] | null;
  Languages?: string[] | null;
  Ethnicities?: string[] | null;

  // Interests and Kinks
  LookingFor?: string[] | null;
  SexualInterests?: string[] | null;
  CommunityKinks?: string[] | null;
  Kinks?: string[] | null;

  // Body Types and Features
  BodyTypes?: string[] | null;
  BodyHair?: string[] | null;
  FacialHair?: string[] | null;
  BodyPartsLiked?: string[] | null;

  // Tag field names that are single-select (only the first value is used)
  // Tag fields not in this array are multi-select
  SingleSelectTags?: string[] | null;

  // Non-tag field names that are unsupported based on tenant settings
  // Tag fields signal unsupported status by being null
  UnsupportedFields?: string[] | null;
}

/**
 * Height value option for height picker
 */
export interface IHeightOption {
  cm: number;
  display: string;
}

/**
 * Predefined height values for the height picker
 */
export const HEIGHT_OPTIONS: IHeightOption[] = [
  { cm: 122, display: "4' 0\" (122 cm)" },
  { cm: 124, display: "4' 1\" (124 cm)" },
  { cm: 127, display: "4' 2\" (127 cm)" },
  { cm: 130, display: "4' 3\" (130 cm)" },
  { cm: 132, display: "4' 4\" (132 cm)" },
  { cm: 135, display: "4' 5\" (135 cm)" },
  { cm: 137, display: "4' 6\" (137 cm)" },
  { cm: 140, display: "4' 7\" (140 cm)" },
  { cm: 142, display: "4' 8\" (142 cm)" },
  { cm: 145, display: "4' 9\" (145 cm)" },
  { cm: 147, display: "4' 10\" (147 cm)" },
  { cm: 150, display: "4' 11\" (150 cm)" },
  { cm: 152, display: "5' 0\" (152 cm)" },
  { cm: 155, display: "5' 1\" (155 cm)" },
  { cm: 157, display: "5' 2\" (157 cm)" },
  { cm: 160, display: "5' 3\" (160 cm)" },
  { cm: 163, display: "5' 4\" (163 cm)" },
  { cm: 165, display: "5' 5\" (165 cm)" },
  { cm: 168, display: "5' 6\" (168 cm)" },
  { cm: 170, display: "5' 7\" (170 cm)" },
  { cm: 173, display: "5' 8\" (173 cm)" },
  { cm: 175, display: "5' 9\" (175 cm)" },
  { cm: 178, display: "5' 10\" (178 cm)" },
  { cm: 180, display: "5' 11\" (180 cm)" },
  { cm: 183, display: "6' 0\" (183 cm)" },
  { cm: 185, display: "6' 1\" (185 cm)" },
  { cm: 188, display: "6' 2\" (188 cm)" },
  { cm: 191, display: "6' 3\" (191 cm)" },
  { cm: 193, display: "6' 4\" (193 cm)" },
  { cm: 196, display: "6' 5\" (196 cm)" },
  { cm: 198, display: "6' 6\" (198 cm)" },
  { cm: 201, display: "6' 7\" (201 cm)" },
  { cm: 203, display: "6' 8\" (203 cm)" },
  { cm: 206, display: "6' 9\" (206 cm)" },
  { cm: 208, display: "6' 10\" (208 cm)" },
  { cm: 211, display: "6' 11\" (211 cm)" },
];

/**
 * Helper to get display string for a height value in cm
 */
export function getHeightDisplay(cm: number | null | undefined): string {
  if (cm === null || cm === undefined) return '';
  const option = HEIGHT_OPTIONS.find((h) => h.cm === cm);
  return option?.display ?? `${cm} cm`;
}
